import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GlobalCTA from "@/components/GlobalCTA";
import { Mic, Presentation, Sparkles, Users } from "lucide-react";
import heroImage from "@/assets/hero-blue-dress.jpg";
import SpeakingInquiryForm from "@/components/SpeakingInquiryForm";
import PageSEO from "@/components/PageSEO";
import { breadcrumb } from "@/lib/schemas";

const Speaking = () => {
  const topics = [
    {
      title: "Wealth as Legacy",
      description: "How to build generational wealth through real estate and strategic financial decisions.",
    },
    {
      title: "The Systems Mindset",
      description: "Breaking free from hustle culture by building businesses that run without you.",
    },
    {
      title: "Faith, Strategy & Freedom",
      description: "Integrating purpose-driven leadership with practical wealth-building strategies.",
    },
    {
      title: "The Digital Nomad Blueprint",
      description: "Using AI and automation to build location-independent empires.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageSEO
        title="Keynote Speaker | AI in Real Estate & Wealth Strategy"
        description="Book Marquetta Bryant for keynotes on AI in real estate, founder psychology, and generational wealth. Available for conferences, brokerages & corporate events."
        canonical="https://marquettabryant.com/speaking"
        jsonLd={[breadcrumb([{name:"Home",path:"/"},{name:"Speaking",path:"/speaking"}])]}
      />
      <Header />
      <main id="main-content">
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-accent font-semibold uppercase tracking-[0.2em] text-sm">
                Book Marquetta
              </span>
              <h1 className="text-4xl md:text-6xl font-bold leading-tight text-white">
                Bold. Transformational.{" "}
                <span className="text-accent">
                  Unforgettable.
                </span>
              </h1>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-xl">
                Marquetta doesn't just deliver speeches — she delivers blueprints. As a licensed real estate broker and Mortgage Advisor, she turns complex financial noise into clear, actionable wealth.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-2xl" />
                <img
                  src={heroImage}
                  alt="Marquetta Bryant - Speaker"
                  className="relative w-80 h-80 md:w-[400px] md:h-[400px] object-cover object-top rounded-full shadow-2xl border-4 border-accent/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speaker Bio */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              The Architect of the{" "}
              <span className="text-accent">Personal Economy</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Known for her bold, transformational, and unforgettable delivery, Marquetta challenges audiences to stop "renting their future" and start engineering their legacy.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Whether she is speaking to rooms full of first-time homebuyers or elite entrepreneurs, her mission remains the same: to prove that <span className="text-foreground font-semibold">success without a successor is failure</span>. She combines the math of a lender with the vision of an architect to show every listener how to command authority and build an empire that outlives them.
            </p>
          </div>
        </div>
      </section>

      {/* Speaking Formats */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Speaking Formats</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From intimate workshops to major conferences, Marquetta adapts to your audience.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Mic, title: "Keynotes", desc: "High-energy presentations that inspire action" },
              { icon: Users, title: "Panels", desc: "Engaging discussions with industry leaders" },
              { icon: Presentation, title: "Workshops", desc: "Hands-on sessions that deliver results" },
            ].map((item, index) => (
              <div key={index} className="p-8 bg-background rounded-2xl text-center hover:shadow-elegant transition-shadow border border-border">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Signature Topics</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {topics.map((topic, index) => (
              <div key={index} className="p-6 bg-muted/30 rounded-xl border border-border">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">{topic.title}</h3>
                    <p className="text-muted-foreground">{topic.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking-form" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Book Marquetta to Speak
              </h2>
              <p className="text-muted-foreground text-lg">
                Ready to ignite your audience? Fill out the form below and we'll be in touch within 48 hours.
              </p>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border shadow-elegant">
              <SpeakingInquiryForm />
            </div>
          </div>
        </div>
      </section>

      <GlobalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Speaking;
